import React, { useMemo, useState, useId } from 'react';
import { Link } from 'react-router-dom';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    FiAlertCircle,
    FiArrowUpRight,
    FiChevronUp,
    FiChevronDown,
    FiSearch,
    FiAlertOctagon,
    FiRefreshCw,
} from 'react-icons/fi';
import WorkflowBadge from './WorkflowBadge';
import { getForecastStepStates, StepStatusCell } from './CheckStatusIndicators';
import {
    formatTiming,
    formatActivityWindow,
    formatShortDate,
    isForecastCheckOverdue,
} from '../utils/dateHelpers';
import { isTriggeredWorkflow } from '../utils/workflowLabels';
import OperationsEmptyState from './OperationsEmptyState';

const text = (value) => (value || '').toString();
const num = (value) => (value === null || value === undefined || value === '' ? Number.POSITIVE_INFINITY : Number(value));

const SORTABLE_COLUMNS = {
    policy_code: (a, b) => text(a.policy_code).localeCompare(text(b.policy_code)),
    reservation_number: (a, b) => text(a.reservation_number).localeCompare(text(b.reservation_number), 'fr', { numeric: true }),
    partner_name: (a, b) => text(a.partner_name).localeCompare(text(b.partner_name), 'fr'),
    client_name: (a, b) => text(a.client_name).localeCompare(text(b.client_name), 'fr'),
    date_to_check: (a, b) => text(a.date_to_check).localeCompare(text(b.date_to_check)),
    timing: (a, b) => (num(a.forecast_timing_day) - num(b.forecast_timing_day))
        || (num(a.forecast_timing_hour) - num(b.forecast_timing_hour)),
    trigger_type: (a, b) => text(a.trigger_type).localeCompare(text(b.trigger_type), 'fr'),
    activity_window: (a, b) => text(a.activity_window?.start).localeCompare(text(b.activity_window?.start)),
    workflow_label: (a, b) => text(a.workflow_label).localeCompare(text(b.workflow_label)),
};

const getRowClassName = (row) => {
    if (row.workflow_label === 'forecast_pending' || row.workflow_label === 'observation_pending') {
        return 'ops-row-pending';
    }
    if (row.workflow_label === 'forecast_checked_triggered_email_pending') {
        return 'ops-row-triggered-pending';
    }
    if (isTriggeredWorkflow(row.workflow_label)) {
        return 'ops-row-triggered';
    }
    return '';
};

const rowKey = (row) => `${row.policy_id}-${row.date_to_check}-${row.check_type}`;

const SortableHeader = ({ column, label, sortConfig, onSort, className = '' }) => {
    const isActive = sortConfig.key === column;
    const direction = isActive ? sortConfig.direction : null;
    const ariaSort = direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';

    return (
        <th scope="col" aria-sort={ariaSort} className={`ops-th ops-th--sortable${isActive ? ' is-sorted' : ''} ${className}`.trim()}>
            <button
                type="button"
                className="ops-th__sort"
                onClick={() => onSort(column)}
                title={`Trier par ${label}`}
            >
                <span>{label}</span>
                <span className="ops-th__sort-icon" aria-hidden="true">
                    {direction === 'desc' ? <FiChevronDown /> : <FiChevronUp />}
                </span>
            </button>
        </th>
    );
};

const Th = ({ children, className = '' }) => (
    <th scope="col" className={`ops-th ${className}`.trim()}>{children}</th>
);

const ModifiedIndicator = ({ modified }) => {
    const tooltipId = useId();
    if (!modified) return <span className="ops-muted" aria-label="Non modifié">—</span>;
    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>Dates de séjour modifiées</Tooltip>}>
            <span className="ops-modified" tabIndex={0} aria-label="Dates de séjour modifiées">
                <FiAlertCircle aria-hidden="true" />
                <span className="ops-modified__label">Oui</span>
            </span>
        </OverlayTrigger>
    );
};

const WORKFLOW_STEPS = [
    { key: 'forecast', label: 'Forecast', description: 'Prévision météo disponible' },
    { key: 'check', label: 'Check', description: 'Check prévision effectué' },
    { key: 'trigger', label: 'Trigger', description: 'Résultat du déclenchement' },
    { key: 'email', label: 'Email', description: 'Email client envoyé' },
    { key: 'claim', label: 'Claim', description: 'Sinistre créé' },
];

const COLUMN_COUNT = 9 + WORKFLOW_STEPS.length + 1;

const SkeletonRows = ({ rows = 6 }) => (
    <>
        {Array.from({ length: rows }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index} className="ops-skeleton-row" aria-hidden="true">
                {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={cell}><span className="ops-skeleton" /></td>
                ))}
            </tr>
        ))}
    </>
);

const ForecastTable = ({
    items = [],
    loading = false,
    refreshing = false,
    compact = false,
    density,
    emptyMessage = 'Aucune policy forecast à contrôler pour cette date',
    emptySubMessage,
    filtered = false,
    onResetFilters,
    error = null,
    onRetry,
    fitContent = false,
    skeletonRows = 6,
}) => {
    const [sortConfig, setSortConfig] = useState({ key: 'date_to_check', direction: 'asc' });
    const [selectedKey, setSelectedKey] = useState(null);
    const resolvedDensity = density || (compact ? 'compact' : 'comfortable');

    const sortedItems = useMemo(() => {
        const sorter = SORTABLE_COLUMNS[sortConfig.key];
        if (!sorter) return items;

        return [...items].sort((a, b) => {
            const result = sorter(a, b);
            return sortConfig.direction === 'asc' ? result : -result;
        });
    }, [items, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const toggleRow = (key) => setSelectedKey((prev) => (prev === key ? null : key));

    const handleRowClick = (event, key) => {
        if (event.target.closest('a, button, [tabindex="0"]:not(tr)')) return;
        toggleRow(key);
    };

    const handleRowKeyDown = (event, key) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleRow(key);
        }
    };

    if (error && !loading && !items.length) {
        return (
            <div className="ops-state ops-state--error" role="alert">
                <FiAlertOctagon className="ops-state__icon" aria-hidden="true" />
                <p className="ops-state__title">Impossible de charger les forecasts</p>
                <p className="ops-state__text">{error}</p>
                {onRetry && (
                    <Button variant="outline-danger" className="ops-btn" onClick={onRetry}>
                        <FiRefreshCw aria-hidden="true" /> Réessayer
                    </Button>
                )}
            </div>
        );
    }

    if (!loading && !sortedItems.length) {
        if (filtered) {
            return (
                <div className="ops-state">
                    <FiSearch className="ops-state__icon" aria-hidden="true" />
                    <p className="ops-state__title">Aucun résultat pour ces filtres</p>
                    <p className="ops-state__text">Modifiez ou retirez certains filtres pour élargir la recherche.</p>
                    {onResetFilters && (
                        <Button variant="outline-secondary" className="ops-btn ops-btn--secondary" onClick={onResetFilters}>
                            Réinitialiser les filtres
                        </Button>
                    )}
                </div>
            );
        }
        return (
            <OperationsEmptyState
                message={emptyMessage}
                subMessage={emptySubMessage}
                compact={compact}
            />
        );
    }

    return (
        <div
            className={`ops-table-wrap ops-table-wrap--${resolvedDensity}${refreshing ? ' is-refreshing' : ''}`}
            aria-busy={loading || refreshing}
        >
            {refreshing && <div className="ops-progress" role="progressbar" aria-label="Actualisation en cours" />}
            <div className={`ops-table-scroll${fitContent ? ' ops-table-scroll--fit' : ''}`} tabIndex={0} aria-label="Tableau des forecasts, défilable horizontalement">
                <table className="ops-table">
                    <thead>
                        <tr>
                            <SortableHeader column="policy_code" label="Code police" sortConfig={sortConfig} onSort={handleSort} className="ops-col-sticky" />
                            <SortableHeader column="reservation_number" label="Réservation" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader column="partner_name" label="Partenaire" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader column="client_name" label="Client" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader column="date_to_check" label="Jour vérifié" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader column="timing" label="Timing" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader column="trigger_type" label="Type trigger" sortConfig={sortConfig} onSort={handleSort} />
                            <Th className="ops-th--center">Modif.</Th>
                            <SortableHeader column="activity_window" label="Dates" sortConfig={sortConfig} onSort={handleSort} />
                            {WORKFLOW_STEPS.map((step) => (
                                <Th key={step.key} className="ops-th--step">
                                    <span title={step.description}>{step.label}</span>
                                </Th>
                            ))}
                            <SortableHeader column="workflow_label" label="État" sortConfig={sortConfig} onSort={handleSort} />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !sortedItems.length ? (
                            <SkeletonRows rows={skeletonRows} />
                        ) : (
                            sortedItems.map((row) => {
                                const key = rowKey(row);
                                const isSelected = selectedKey === key;
                                const steps = getForecastStepStates(row);
                                return (
                                    <tr
                                        key={key}
                                        className={`${getRowClassName(row)}${isSelected ? ' is-selected' : ''}`}
                                        onClick={(e) => handleRowClick(e, key)}
                                        onKeyDown={(e) => handleRowKeyDown(e, key)}
                                        tabIndex={0}
                                        aria-selected={isSelected}
                                    >
                                        <td data-label="Code police" className="ops-col-sticky ops-cell-primary">
                                            {row.policy_id ? (
                                                <Link
                                                    to={`/admin/trips/policies/${row.policy_id}`}
                                                    className="ops-policy-link"
                                                    title="Ouvrir le détail de la police"
                                                >
                                                    <span>{row.policy_code || '—'}</span>
                                                    <FiArrowUpRight className="ops-policy-link__icon" aria-hidden="true" />
                                                </Link>
                                            ) : (
                                                <span className="ops-mono">{row.policy_code || '—'}</span>
                                            )}
                                        </td>
                                        <td data-label="Réservation" className="ops-cell-secondary ops-mono">{row.reservation_number || '—'}</td>
                                        <td data-label="Partenaire" className="ops-cell-strong ops-cell-wrap">{row.partner_name || '—'}</td>
                                        <td data-label="Client" className="ops-cell-wrap">{row.client_name || '—'}</td>
                                        <td data-label="Jour vérifié" className="ops-cell-strong ops-num">{formatShortDate(row.date_to_check)}</td>
                                        <td data-label="Timing" className="ops-num">{formatTiming(row.forecast_timing_day, row.forecast_timing_hour)}</td>
                                        <td data-label="Type trigger" className="ops-cell-secondary">{row.trigger_type || '—'}</td>
                                        <td data-label="Modif." className="ops-td--center">
                                            <ModifiedIndicator modified={row.trip_modified} />
                                        </td>
                                        <td data-label="Dates" className="ops-cell-secondary ops-num ops-nowrap">{formatActivityWindow(row.activity_window)}</td>
                                        {WORKFLOW_STEPS.map((step) => (
                                            <td key={step.key} data-label={step.label} className="ops-td--step">
                                                <StepStatusCell label={step.description} state={steps?.[step.key]} />
                                            </td>
                                        ))}
                                        <td data-label="État">
                                            <WorkflowBadge
                                                label={row.workflow_label}
                                                context="forecast"
                                                status={row.status}
                                                overdue={row.workflow_label === 'forecast_pending' && isForecastCheckOverdue(row)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ForecastTable;
